import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { apiService } from "../services/api";
import { useAuth } from "./AuthContext";

interface TenantCustomization {
  logo?: string;
  background?: string;
  logoUrl?: string;
  backgroundUrl?: string;
}

interface TenantContextType {
  customization: TenantCustomization;
  refreshCustomization: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const { state: authState } = useAuth();
  const [customization, setCustomization] = useState<TenantCustomization>({});

  const loadCustomization = useCallback(async () => {
    // Only load for tenant users (not super_admin)
    if (!authState.isAuthenticated || authState.user?.role === "super_admin") {
      setCustomization({});
      return;
    }

    try {
      const response = await apiService.getCompanySettings();
      const settings = response.data;

      // Use logo_url and background_url diretamente do backend (já vem com URL completa)
      const newCustomization = {
        logo: settings.logo,
        background: settings.background,
        logoUrl: settings.logo_url ? settings.logo_url : undefined,
        backgroundUrl: settings.background_url ? settings.background_url : undefined,
      };

      setCustomization(newCustomization);
    } catch (error: any) {
      setCustomization({});
    }
  }, [authState.isAuthenticated, authState.user?.role]);

  useEffect(() => {
    if (authState.isAuthenticated) {
      loadCustomization();
    } else {
      setCustomization({});
    }
  }, [authState.isAuthenticated, loadCustomization]);

  const value: TenantContextType = {
    customization,
    refreshCustomization: loadCustomization,
  };

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

export function useTenant(): TenantContextType {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}
